import * as React from 'react';
import { useState, Fragment } from 'react';
import { format } from 'date-fns';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    BooleanField,
    TopToolbar,
    useNotify,
    useDeleteMany,
    useUnselectAll,
} from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import useAdmin from '../hooks/useAdmin';
import { UserRole } from '../types/types';

interface ProductPricingRecord {
    [key: string]: any;
    id: number;
    product_id: number;
    user_id: number;
    price_date: Date;
    price: number;
    company_name: string;
    is_deleted?: boolean | string | number;
    dt_created?: string;
    dt_updated?: string;
}

const isRecordDeleted = (record?: ProductPricingRecord): boolean => {
    const value = record?.is_deleted;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        return value === 't' || value === 'true' || value === '1';
    }
    return false;
};

const CustomDateField: React.FC<{ record?: ProductPricingRecord; source: string }> = ({ record, source }) => (
    record && record[source] ? (
        <span>{format(new Date(record[source]), 'dd-MM-yyyy HH:mm')}</span>
    ) : null
);

const DeletedField: React.FC<{ record?: ProductPricingRecord }> = ({ record }) => (
    <span>{isRecordDeleted(record) ? 'Yes' : ''}</span>
);

const deletedRowSx = (record: ProductPricingRecord) => (
    isRecordDeleted(record)
        ? { backgroundColor: '#ececec !important', color: '#616161' }
        : {}
);

const CustomToolbar: React.FC = () => {
    const navigate = useNavigate();
    useAdmin();
    return (
        <TopToolbar>
            <IconButton onClick={() => navigate('/products')}>
                <ArrowBack />
            </IconButton>
        </TopToolbar>
    );
};

const BulkDeleteWithConfirmButton: React.FC<any> = (props) => {
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const [deleteMany, { isLoading }] = useDeleteMany();
    const unselectAll = useUnselectAll('productHistory');

    const role_id = localStorage.getItem('user_role_id');
    const userRole = role_id ? parseInt(role_id, 10) as UserRole : null;

    const handleOpen = () => {
        if (userRole === UserRole.Admin) {
            setOpen(true);
        } else {
            alert('Not Authorized. Contact your Administrator');
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = async () => {
        deleteMany('productHistory', { ids: props.selectedIds }).then(() => {
            notify('Pricing records marked as deleted', { type: 'info' });
            unselectAll();
            setOpen(false);
        }).catch((error) => {
            notify(`Error: ${error.message}`, { type: 'warning' });
        });
    };

    return (
        <Fragment>
            <Button
                onClick={handleOpen}
                startIcon={<DeleteIcon />}
                disabled={isLoading || props.selectedIds.length === 0}
                color="error"
            >
                Mark deleted
            </Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Confirm delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Mark the selected pricing records as deleted? They will remain visible here in gray for audit.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">Cancel</Button>
                    <Button onClick={handleDelete} color="secondary">Mark deleted</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

const ProductHistory: React.FC<any> = (props) => (
    <List
        {...props}
        actions={<CustomToolbar />}
        bulkActionButtons={<BulkDeleteWithConfirmButton />}
        sort={{ field: 'price_date', order: 'DESC' }}
    >
        <Datagrid rowSx={deletedRowSx}>
            <TextField source="company_name" label="Seller" />
            <TextField source="city_name" label="Ware House" />
            <TextField source="price" label="Price" />
            <FunctionField
                source="price_date"
                label="Price Update Date"
                render={(record: ProductPricingRecord) => <CustomDateField record={record} source="price_date" />}
            />
            <FunctionField
                source="dt_updated"
                label="Last updated / removed"
                render={(record: ProductPricingRecord) => <CustomDateField record={record} source="dt_updated" />}
            />
            <FunctionField label="Deleted" render={(record: ProductPricingRecord) => <DeletedField record={record} />} />
            <BooleanField source="is_lowest" label="Is Lowest" FalseIcon={null} />
        </Datagrid>
    </List>
);

export default ProductHistory;
